// Number formatting for the home "impact" panel (growth since you started + time saved).

import type { ImpactGrowth, ImpactReport } from "@gmx/shared/features/impact";
import { timeValue } from "@gmx/shared/features/impact";

const nf = new Intl.NumberFormat("en-US");
const one = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

/** Saved time as a big number + its unit: "45" min, "4.5" hours, "1,240" hours. */
export function duration(seconds: number): { value: string; unit: string } {
  if (seconds <= 0) return { value: "0", unit: "hours" };
  if (seconds < 3600) {
    const m = Math.max(1, Math.round(seconds / 60));
    return { value: nf.format(m), unit: m === 1 ? "minute" : "minutes" };
  }
  const h = seconds / 3600;
  const value = h < 10 ? one.format(Math.round(h * 10) / 10) : nf.format(Math.round(h));
  return { value, unit: value === "1" ? "hour" : "hours" };
}

/** "4.5 hours", "45 minutes". */
export function durationText(seconds: number): string {
  const d = duration(seconds);
  return `${d.value} ${d.unit}`;
}

/** US dollars: "$4.50" under $10, then whole dollars, compact from a million. */
export function money(dollars: number): string {
  if (dollars >= 1_000_000) return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(dollars);
  const small = dollars > 0 && dollars < 10;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: small ? 2 : 0, maximumFractionDigits: small ? 2 : 0 }).format(dollars);
}

/** What the saved time is worth at this rate. */
export function worth(seconds: number, hourlyRate: number): string {
  return money(timeValue(seconds, hourlyRate));
}

/** 0.25 → "+25%", 1 → "+100%", 0.034 → "+3.4%", −0.02 → "−2%". */
export function pct(v: number): string {
  const p = v * 100;
  const abs = Math.abs(p);
  const body = abs >= 100 ? nf.format(Math.round(abs)) : abs >= 10 ? nf.format(Math.round(abs)) : one.format(Math.round(abs * 10) / 10);
  return `${p > 0 ? "+" : p < 0 ? "−" : ""}${body}%`;
}

/** +12 / −3 / 0 with a thousands separator. */
export function signed(n: number): string {
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${nf.format(Math.abs(n))}`;
}

/** Per-task by-hand time: "8 sec", "90 sec", "5 min". */
export function each(seconds: number): string {
  return seconds < 120 ? `${seconds} sec` : `${one.format(seconds / 60)} min`;
}

/** The headline growth figure: a percentage when there's a starting count, else the raw gain. */
export function growthFigure(g: ImpactGrowth): string | null {
  if (g.followerGrowth === null) return null;
  if (g.followerGrowthPct !== null) return pct(g.followerGrowthPct);
  return signed(g.followerGrowth);
}

export type Mood = "growing" | "saving" | "flat" | "fresh";

/** What the headline leads with: growth when there is some, saved time otherwise. */
export function mood(r: Pick<ImpactReport, "growth" | "time">): Mood {
  const grew = (r.growth.followerGrowth ?? 0) > 0;
  if (grew) return "growing";
  if (r.time.totalSeconds > 0) return "saving";
  if (r.growth.then.followers !== null) return "flat";
  return "fresh";
}

/** "today", "1 day", "12 days". */
export function daysText(days: number): string {
  if (days <= 0) return "today";
  return `${nf.format(days)} ${days === 1 ? "day" : "days"}`;
}
